import { feedKey } from '../lib/feedContext';
/**
 * Окно одной вилки из уже живой ленты, а не новым заходом в приложение.
 *
 * До 13.08.2026 клик открывал `/app?fork=`: новый документ, новый бандл, новый
 * сокет. Пока это собиралось, человек видел «собираем вилки» несколько секунд —
 * хотя карточку он только что смотрел в ленте. Новый сокет ещё и отбирал место
 * у живой ленты: одна лента на человека, побеждает новый вход.
 *
 * Ручка `window.open` без `noopener` даёт документ `about:blank`, и в него
 * встаёт та же карточка, тот же стор, тот же сокет. Адрес `?fork=` остаётся
 * запасным: если браузер не отдал окно, открывается прежний путь.
 *
 * Калькулятор — второе окно той же вилки: две строки, те же замки. Выносят
 * через Document Picture-in-Picture, если браузер умеет: у PiP нет адресной
 * строки, у `window.open` браузер её не отдаёт — это не стиль, а антиспуфинг.
 * Иначе обычный попап без обещания «поверх». Вилка ушла или ушла в минус —
 * окно само не закрываем: числа ещё нужны, пока ставят вторую ногу.
 * `popped` общий: закрыли карточку в ленте, а виджет ещё на экране — замок жив.
 * Снимаем `popped`, когда не осталось ни карточки, ни калькулятора.
 */

import { createElement } from 'react';
import { createRoot, type Root } from 'react-dom/client';

import { CalcFloat } from './Calc';
import { One } from './One';
import { pipHost } from './pip';
import { leaveSeed, takeSeed } from './forkSeed';
import { forkHref } from './route';
import { loadLayout } from './deskLayout';
import { paintSkin, readSkin } from './skin';
import { useScreen } from './store';

export { leaveSeed, takeSeed };

const FEATURES = 'popup=yes,width=980,height=880';
/** Компактное окошко: две строки и подвал, без пустого поля. */
export const CALC_WIDTH = 340;
/** PiP — только холст. Попап ест высоту на адресную строку, которую не убрать. */
const BAR_PIP_HEIGHT = 132;
const BAR_POP_HEIGHT = 200;
const FORK_PIP_HEIGHT = 430;
const FORK_POP_HEIGHT = 450;

function calcBox(): { pipH: number; popH: number; features: string; root: string } {
  const fork = loadLayout().calcSkin === 'fork';
  const popH = fork ? FORK_POP_HEIGHT : BAR_POP_HEIGHT;
  return {
    pipH: fork ? FORK_PIP_HEIGHT : BAR_PIP_HEIGHT,
    popH,
    features: `popup=yes,width=${CALC_WIDTH},height=${popH}`,
    root: fork ? 'nx-root--fork' : 'nx-root--bar',
  };
}
const opening = new Set<string>();

type Kind = 'card' | 'calc';

interface Pop {
  win: Window;
  root: Root;
  watch: number;
}

interface Held {
  card?: Pop;
  calc?: Pop;
}

const live = new Map<string, Held>();

function unmountPop(pop: Pop | undefined): void {
  if (pop === undefined) return;
  window.clearInterval(pop.watch);
  try {
    pop.root.unmount();
  } catch {
    // Окно уже закрыто — корня нет.
  }
}

function releaseIfIdle(id: string): void {
  const slot = live.get(id);
  if (slot !== undefined && (slot.card !== undefined || slot.calc !== undefined)) return;
  live.delete(id);
  useScreen.getState().unpop(id);
}

function dropKind(id: string, kind: Kind): void {
  const slot = live.get(id);
  if (slot === undefined) return;
  unmountPop(slot[kind]);
  delete slot[kind];
  releaseIfIdle(id);
}

function drop(id: string): void {
  const slot = live.get(id);
  live.delete(id);
  useScreen.getState().unpop(id);
  if (slot === undefined) return;
  unmountPop(slot.card);
  unmountPop(slot.calc);
}

function dress(doc: Document, rootExtra: string): HTMLElement {
  for (const node of document.head.querySelectorAll(
    'link[rel="stylesheet"], link[rel="preconnect"], style',
  )) {
    doc.head.appendChild(node.cloneNode(true));
  }
  try {
    if (document.adoptedStyleSheets.length > 0) {
      doc.adoptedStyleSheets = document.adoptedStyleSheets;
    }
  } catch {
    // jsdom и часть браузеров adoptedStyleSheets чужому документу не отдают.
  }
  paintSkin(readSkin(), doc);
  doc.body.className = 'nx-body';
  doc.body.style.margin = '0';
  let host = doc.getElementById('fork-root');
  if (host === null) {
    host = doc.createElement('div');
    host.id = 'fork-root';
    host.className = `nx-root ${rootExtra}`;
    doc.body.appendChild(host);
  } else {
    host.className = `nx-root ${rootExtra}`;
  }
  return host;
}

function fallback(id: string): void {
  leaveSeed(id);
  window.open(forkHref(id), feedKey(`forq-fork-${id}`));
}

function paint(id: string, win: Window, kind: Kind): void {
  let host: HTMLElement;
  try {
    if (win.document.getElementById('root') !== null) {
      // Имя окна совпало со сканером — не вытираем ленту и не оставляем
      // чёрный `#root` вместо калькулятора. Для виджета открываем безымянный лист.
      if (kind === 'calc') {
        const fresh = window.open('about:blank', '_blank', calcBox().features);
        if (fresh !== null && fresh !== win) paint(id, fresh, 'calc');
        return;
      }
      win.focus();
      return;
    }
    host = dress(win.document, kind === 'calc' ? calcBox().root : 'nx-root--one');
    if (kind === 'calc') {
      try {
        win.resizeTo(CALC_WIDTH, calcBox().popH);
      } catch {
        // Яндекс и Chrome помнят прошлый размер — без этого окно остаётся огромным.
      }
    }
    try {
      win.document.title = kind === 'calc' ? 'калькулятор' : 'вилка';
    } catch {
      // Чужой документ иногда не даёт title — окно всё равно рисуем.
    }
  } catch {
    if (kind === 'card') fallback(id);
    return;
  }
  dropKind(id, kind);
  // Подпись «тут будет вилка» или прежняя вилка этого окна — не под новым корнем.
  host.replaceChildren();
  const root = createRoot(host);
  const watch = window.setInterval(() => {
    if (win.closed) dropKind(id, kind);
  }, 400);
  const slot = live.get(id) ?? {};
  slot[kind] = { win, root, watch };
  live.set(id, slot);
  useScreen.getState().pop(id);
  if (kind === 'calc') {
    root.render(createElement(CalcFloat, { id, frame: win }));
  } else {
    root.render(createElement(One, { id, hold: 'pop', frame: win }));
  }
  win.focus();
}

/** Вставить карточку в уже открытое окно. Повтор той же вилки не плодит второй корень. */
export function placeCard(id: string, win: Window): void {
  const existing = live.get(id)?.card;
  if (existing !== undefined && !existing.win.closed && existing.win === win) {
    win.focus();
    return;
  }
  if (existing !== undefined) dropKind(id, 'card');
  // Одно окно на все вилки стола: новая сменяет прежнюю, двух корней в окне нет.
  for (const [other, held] of [...live]) {
    if (other !== id && held.card?.win === win) dropKind(other, 'card');
  }
  paint(id, win, 'card');
}

/** Окно карточки до первой вилки: подпись, а не белый лист. */
export function placeHolder(win: Window): void {
  try {
    if ([...live.values()].some((held) => held.card?.win === win)) return;
    const host = dress(win.document, 'nx-root--one');
    win.document.title = 'вилка';
    host.replaceChildren();
    const note = win.document.createElement('p');
    note.textContent = 'тут будет вилка';
    note.style.cssText = 'margin:0;padding:32px 24px;font-size:16px;opacity:.6';
    host.appendChild(note);
  } catch {
    // Чужое окно — подписи не будет, вилка всё равно встанет по клику.
  }
}

/**
 * Открыть вилку отдельным окном.
 *
 * Размер. Было 560×720 — колонка в телефон шириной, и две ноги в ней вставали
 * друг под друга: окно ради параллельной работы с двумя конторами показывало их
 * последовательно (08.08, владелец: «оно чуть ли не телефонной версией
 * открывается»). Обе плитки рядом требуют примерно 900 px, поэтому окно почти
 * квадратное. Оно всё ещё меньше экрана: его кладут ПОВЕРХ сайта конторы, и
 * закрыть форму ставки целиком оно не должно.
 *
 * Имя окна — по вилке: второе нажатие на ту же строку поднимает уже открытое
 * окно, а не плодит копии.
 */
export function popOut(id: string): void {
  const existing = live.get(id)?.card;
  if (existing !== undefined && !existing.win.closed) {
    existing.win.focus();
    return;
  }
  const win = window.open('about:blank', feedKey(`forq-fork-${id}`), FEATURES);
  if (win === null) {
    fallback(id);
    return;
  }
  paint(id, win, 'card');
}

/**
 * Узкий калькулятор поверх вкладки конторы.
 *
 * Не карточка: плиток и «открыть обе» нет, две строки кэф · сумма · доход.
 * Числа из того же стора, что лента. Picture-in-Picture — максимум «поверх»;
 * нет API или отказ — обычный попап без этого обещания. Два сразу не обещаем.
 */
export function popCalc(id: string, onOpened?: (ok: boolean) => void): void {
  void openCalc(id).then(ok => onOpened?.(ok)).catch(() => onOpened?.(false));
}

async function openCalc(id: string): Promise<boolean> {
  const existing = live.get(id)?.calc;
  if (existing !== undefined && !existing.win.closed) {
    existing.win.focus();
    return true;
  }
  if (opening.has(id)) return false;
  opening.add(id);
  try {
    const pip = pipHost();
    if (pip !== null) {
      try {
        const box = calcBox();
        const win = await pip.requestWindow({
          width: CALC_WIDTH,
          height: box.pipH,
          disallowReturnToOpener: true,
        });
        paint(id, win, 'calc');
        return true;
      } catch {
        // Жест потеряли, отказали, PiP занят — обычное окно, без «поверх».
      }
    }
    const win = window.open('about:blank', feedKey(`forq-calc-${id}`), calcBox().features);
    if (win === null) return false;
    paint(id, win, 'calc');
    return true;
  } finally {
    opening.delete(id);
  }
}

/** Снять все порталы: тесты не оставляют корни после себя. */
export function closePops(): void {
  for (const id of [...live.keys()]) drop(id);
}

/** Узкий калькулятор этой вилки ещё на экране — карточке нельзя снять `popped`. */
export function calcOpen(id: string): boolean {
  const pop = live.get(id)?.calc;
  return pop !== undefined && !pop.win.closed;
}
